/**
 * Social-research SSE pipeline → store subscriber.
 *
 * Translates `social:*` typed events into department-store mutations.
 * Migrated in Phase 8 commit 74.
 */

import { sseEvents } from "./sseEvents";
import { mapSocialResearchDoneData } from "../store/department/utils";
import type {
  CampaignData,
  CoAgentMessage,
  StepStatus,
} from "../store/department/types";

interface SocialSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
}

let wired = false;

export function wireSocialSubscriber(getHooks: () => SocialSubscriberHooks): void {
  if (wired) return;
  wired = true;

  sseEvents.subscribe("social:session-named", ({ name }) => {
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("social:query-plan-complete", ({ data }) => {
    const hooks = getHooks();
    const queryCount = Number(data.query_count ?? 0);
    hooks.addStepLog(1, `[SUCCESS] Generated ${queryCount} social research queries`);
    hooks.updateCampaignData({
      socialQueryPlan: data.query_plan as CampaignData["socialQueryPlan"],
      socialResearchPhase: "social_data_collection",
      socialResearchProgress: {
        phase: "query_plan",
        completed: queryCount,
        total: queryCount,
      },
    });
  });

  sseEvents.subscribe(
    "social:data-collection-progress",
    ({ track, completed, total }) => {
      getHooks().updateCampaignData({
        socialResearchPhase: "social_data_collection",
        socialResearchProgress: { phase: track, completed, total },
      });
    },
  );

  sseEvents.subscribe("social:data-collection-complete", ({ data }) => {
    const hooks = getHooks();
    const successful = Number(data.successful ?? 0);
    const videosCollected = Number(data.videos_collected ?? 0);
    const transcriptsExtracted = Number(data.transcripts_extracted ?? 0);
    const durationMs = Number(data.duration_ms ?? 0);
    hooks.addStepLog(
      1,
      `[SUCCESS] Collected ${successful} results (${videosCollected} videos, ${transcriptsExtracted} transcripts) in ${Math.round(durationMs / 1000)}s`,
    );
    hooks.updateCampaignData({
      socialResearchPhase: "distillation",
      videosCollected,
      transcriptsExtracted,
      engagementBenchmarks: data.engagement_benchmarks as CampaignData["engagementBenchmarks"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "running" },
    }));
  });

  sseEvents.subscribe("social:distillation-complete", ({ data }) => {
    const hooks = getHooks();
    const findingsCount = Number(data.findings_count ?? 0);
    const sections = Number(data.sections ?? 0);
    hooks.addStepLog(
      2,
      `[SUCCESS] Distilled ${findingsCount} findings across ${sections} sections`,
    );
    hooks.updateCampaignData({ socialResearchPhase: "trend_analysis" });
  });

  sseEvents.subscribe("social:trend-analysis-complete", ({ data }) => {
    const hooks = getHooks();
    const trendsFound = Number(data.trends_found ?? 0);
    hooks.addStepLog(2, `[SUCCESS] Identified ${trendsFound} platform trends`);
    hooks.setStepState((state) => ({
      currentStepIndex: 3,
      stepStatuses: { ...state.stepStatuses, 2: "completed", 3: "running" },
    }));
    hooks.setFocusedStep(2);
  });

  sseEvents.subscribe("social:competitor-audit-complete", ({ data }) => {
    const hooks = getHooks();
    const competitorsAnalyzed = Number(data.competitors_analyzed ?? 0);
    const gapsFound = Number(data.gaps_found ?? 0);
    hooks.addStepLog(
      3,
      `[SUCCESS] Audited ${competitorsAnalyzed} competitors, found ${gapsFound} content gaps`,
    );
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: { ...state.stepStatuses, 3: "completed", 4: "running" },
    }));
    hooks.setFocusedStep(3);
  });

  sseEvents.subscribe("social:template-generation-complete", ({ data }) => {
    const hooks = getHooks();
    const templatesCreated = Number(data.templates_created ?? 0);
    hooks.addStepLog(4, `[SUCCESS] Created ${templatesCreated} video templates`);
    hooks.setStepState((state) => ({
      currentStepIndex: 5,
      stepStatuses: { ...state.stepStatuses, 4: "completed", 5: "running" },
    }));
    hooks.setFocusedStep(4);
  });

  sseEvents.subscribe("social:calendar-generation-complete", ({ data }) => {
    const hooks = getHooks();
    const weeks = Number(data.weeks ?? 0);
    const totalPosts = Number(data.total_posts ?? 0);
    hooks.addStepLog(5, `[SUCCESS] Built ${weeks}-week calendar with ${totalPosts} posts`);
    hooks.setStepState((state) => ({
      currentStepIndex: 6,
      stepStatuses: { ...state.stepStatuses, 5: "completed", 6: "running" },
    }));
    hooks.setFocusedStep(5);
  });

  sseEvents.subscribe("social:brief-generation-complete", ({ data }) => {
    const hooks = getHooks();
    const briefsGenerated = Number(data.briefs_generated ?? 0);
    hooks.addStepLog(6, `[SUCCESS] Generated ${briefsGenerated} video briefs for Week 1`);
    hooks.setStepState((state) => ({
      currentStepIndex: 6,
      stepStatuses: { ...state.stepStatuses, 6: "completed" },
    }));
    hooks.setFocusedStep(6);
  });

  sseEvents.subscribe("social:done", ({ data }) => {
    const hooks = getHooks();
    const collectionStats = data.collection_stats as { successful?: number } | undefined;
    hooks.updateCampaignData({
      socialResearchStatus: "complete",
      socialResearchPhase: "done",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...mapSocialResearchDoneData(data as Record<string, any>),
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 7,
      stepStatuses: { ...state.stepStatuses, 7: "waiting" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `Social research complete! I analyzed content across ${
        collectionStats?.successful || 0
      } sources. Here's your trend report:`,
      widget: "trendReport",
    });
  });

  sseEvents.subscribe("social:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ socialResearchStatus: "error", socialResearchPhase: "error" });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `Social research encountered an error: ${message}`,
    });
  });
}
